import React, { useState, useEffect } from "react";
import { Star, X } from "lucide-react";
import VideoLoader from "./VideoLoader";
import axios from "axios";

const SURVEY_URL = process.env.REACT_APP_SURVEY_URL;
const SURVEY_ID = process.env.REACT_APP_SURVEY_ID;

interface SurveyField {
  id: string;
  type: string;
  label: string;
  required: boolean;
  order: number;
  config: Record<string, unknown>;
}

export interface FoodRatingModalProps {
  item: { itemId: string; name: string };
  orderId: string;
  onClose: () => void;
  onSubmitted: () => void;
  userId?: string;
}

const FoodRatingModal: React.FC<FoodRatingModalProps> = ({
  item,
  orderId,
  onClose,
  onSubmitted,
  userId,
}) => {
  const [fields, setFields] = useState<SurveyField[]>([]);
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingFields, setLoadingFields] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const res = await axios.get(`${SURVEY_URL}/survey/${SURVEY_ID}`);
        const surveyFields: SurveyField[] =
          res.data.data?.fields || res.data.fields || [];
        setFields(surveyFields);
      } catch {
        console.error("Failed to load survey fields");
      } finally {
        setLoadingFields(false);
      }
    };
    fetchSurvey();
  }, []);

  const ratingField = fields.find((f) => f.type === "rating");
  const textField = fields.find(
    (f) =>
      f.type === "textarea" || f.type === "text" || f.type === "description",
  );

  const handleSubmit = async () => {
    if (rating === 0) return;
    setSubmitting(true);
    try {
      const answers: { fieldId: string; value: unknown; itemId?: string }[] =
        [];
      if (ratingField) {
        answers.push({
          fieldId: ratingField.id,
          value: rating,
          itemId: item.itemId,
        });
      }
      if (textField && description.trim()) {
        answers.push({
          fieldId: textField.id,
          value: description.trim(),
          itemId: item.itemId,
        });
      }

      await axios.post(`${SURVEY_URL}/surveyResponse/submit/${SURVEY_ID}`, {
        surveyId: SURVEY_ID,
        orderId,
        respondedBy: userId || null,
        answers,
      });

      setSubmitted(true);
      setTimeout(() => {
        onSubmitted();
        onClose();
      }, 1200);
    } catch {
      console.error("Failed to submit rating");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-[18px] font-bold text-black">Rate {item.name}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
          >
            <X size={18} />
          </button>
        </div>

        {loadingFields ? (
          <div className="flex justify-center py-8">
            <VideoLoader size="small" />
          </div>
        ) : submitted ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-2">✅</div>
            <p className="text-[16px] font-semibold text-black">
              Thank you for your feedback!
            </p>
          </div>
        ) : (
          <>
            {/* Star Rating */}
            <div className="mb-5">
              <label className="text-[14px] font-medium text-black/70 mb-2 block">
                {ratingField?.label || "How was this item?"}
              </label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className="p-1 transition-transform hover:scale-110"
                    onMouseEnter={() => setHoveredStar(star)}
                    onMouseLeave={() => setHoveredStar(0)}
                    onClick={() => setRating(star)}
                  >
                    <Star
                      size={32}
                      className={`transition-colors ${
                        star <= (hoveredStar || rating)
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="mb-5">
              <label className="text-[14px] font-medium text-black/70 mb-2 block">
                {textField?.label || "Tell us more (optional)"}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What did you like or dislike?"
                className="w-full border border-black/20 rounded-xl p-3 text-[14px] resize-none h-24 focus:outline-none focus:border-black/40"
              />
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={rating === 0 || submitting}
              className={`w-full py-3 rounded-full font-semibold text-[15px] transition ${
                rating === 0
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-main/90 text-white dm-white-btn"
              }`}
            >
              {submitting ? "Submitting..." : "Submit Rating"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default FoodRatingModal;
